import { useLocation } from 'react-router';

import { cn } from '@rocco/utils/cn';
import { useProfessionalInitialRouteData } from '@modules/professional/hook/use-professional-initial-route-data';
import { professionalTabBase } from '@modules/professional/util/professional-tab-base';
import { useMatchesHandleData } from '~/modules/root/hook';
import {
    DetailSaveButton,
    detailCta,
    TrackedWebsiteCta,
} from '~/ui-rocco/pages/detail-shared';
import { CONTENT_CAP_PADDED } from '~/ui-rocco/pages/detail-shared/large-screen';
import { useProfessionalPrimaryCta } from '../ProfessionalPageCtasProvider';

/** The product page's Save button surface: white, lifted, no hairline. */
const SAVE_LIFTED =
    'border-transparent bg-white text-[#181818] [box-shadow:0px_4px_14px_10px_rgba(0,0,0,0.05)] hover:bg-white/70';

/**
 * The product page's CTA cluster on the profile: the practice's own enquiry
 * label (Get in Touch, Request a Quote — whatever they configured), Visit
 * Website and Save, ranged right under the badge, level with the statement's
 * first line as the product's are — and sticky, so once the
 * page has scrolled them up to the tab band they lock into its right end and
 * ride there for the rest of the page.
 *
 * A page-level sibling rather than part of the summary block: a sticky
 * element only travels as far as its own parent, and inside that block it
 * would come unstuck the moment the block scrolled away.
 *
 * The margins are what let it sit on the statement's line without occupying
 * it: the top one puts the pair 145px under the banner, where the product's
 * CTAs sit under its picture (the band is 92px tall against the trail's 65,
 * so 45 here is the product's 80 there); the bottom one gives the pair's own
 * height back, so the summary starts exactly where it otherwise would. The lock point is the band's midline: the band is
 * 92px tall and the pill 52px, so 20px down puts the two on one centre.
 *
 * Only the Overview has the statement to share a line with. On every other
 * tab the pair starts on the band's line instead, left of the badge where the
 * Overview shows the profile's figures, so the tab's content opens directly
 * under the band as before.
 */
export const ProfessionalStickyCtas = () => {
    const initial = useProfessionalInitialRouteData();
    const { pathname } = useLocation();
    const isBMPreview = useMatchesHandleData('isBMPreview', false);
    // Public page: re-enable clicks under the sticky pointer-events-none strip.
    // BM preview: leave them off so ProfessionalPagePreview's lock holds (BC-5305).
    const ctaPointer = !isBMPreview && 'pointer-events-auto';
    // The header CTA following the reader down the page, so it reads and
    // opens the same thing.
    const primary = useProfessionalPrimaryCta({
        source: 'ProfessionalStickyCTA',
    });
    if (!initial) return null;

    // Preview-aware: a hardcoded public path never matches inside the
    // Business Manager preview, which gave the pair the non-overview
    // geometry on the preview's overview and rode it up over the band.
    const overview =
        pathname.replace(/\/+$/, '') ===
        professionalTabBase(initial.URLSegment, pathname);

    return (
        <div
            className={cn(
                'pointer-events-none sticky z-[120] w-full max-md:hidden md:top-[1.25rem] md:px-[3rem]',
                overview
                    ? 'md:mt-[2.8125rem] md:mb-[-3.25rem]'
                    : 'md:-mt-[4.5rem] md:mb-[1.25rem]',
            )}
        >
            <div
                className={cn(
                    CONTENT_CAP_PADDED,
                    'flex items-center justify-end gap-2',
                    // 48px clear of the badge's column, as the figures are.
                    !overview && 'md:pr-[16.5625rem]',
                )}
            >
                {primary.visible ? (
                    <button
                        type="button"
                        onClick={primary.open}
                        className={detailCta('white-lifted', ctaPointer)}
                    >
                        {primary.label}
                    </button>
                ) : null}
                {primary.showInlineBook ? (
                    <button
                        type="button"
                        onClick={primary.book.open}
                        className={detailCta('white-lifted', ctaPointer)}
                    >
                        {primary.book.label}
                    </button>
                ) : null}
                {/* The shared referral CTA, not a plain link: the website URL
                    arrives from GraphQL already carrying UTM, but the reader's
                    own `__apid` is only known in the browser, and a raw link
                    sent the supplier a referral with no one attached to it.
                    This also fires the `Website` event the reporting counts.

                    `ctaPointer`, not a hardcoded `pointer-events-auto`: the
                    preview lock (BC-5305) has to hold on this button too, or
                    it would be the one CTA still clickable in Business
                    Manager's preview while the other two are inert. */}
                <TrackedWebsiteCta
                    website={initial.Website}
                    libraryItemID={initial.ID}
                    professionalID={initial.ID}
                    professionalTitle={initial.Title}
                    source="ProfileStickyWebsite"
                    variant="white-lifted"
                    className={ctaPointer || undefined}
                />
                <DetailSaveButton
                    pinId={initial.ID}
                    pinType="ProfessionalPin"
                    hoverLabel
                    className={cn(SAVE_LIFTED, ctaPointer)}
                />
            </div>
        </div>
    );
};
